import React, { useContext, useEffect, useState } from 'react'
import * as yup from 'yup'
import styled from 'styled-components'
import { DateOfBirthValues } from '@zuto/molecules'
import { useCallbackOnce } from '@zuto/utilities'
import { GridItem } from '@zuto/core/src/grid'
import Box from '@zuto/core/src/box'
import MuiDateOfBirth from '@zuto/molecules/src/Mui/DateOfBirth/MuiDateOfBirth'
import QuestionComponentProps from '../../../Questions/question-component-props'
import DateOfBirthValidator from '../../../validators/DateOfBirthValidator'
import QuestionKey from '../../../Questions/question-key'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import { handlePrevButtonClick, questionFontStyle, fireReactGAEvent } from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import { AttributionContext } from '../../../context'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { isMobileOrTabletDevice } from '../../../utils/detect-device'
import { calculateDOBCategory } from '../../../utils/utils'

const QuestionTitleWrapper = styled(GridItem)`
  display: flex;
  align-items: center;
  &.questionCopyChangeV1 {
    flex-direction: column;
    align-items: flex-start;
    > div {
      margin-bottom: 0;
    }
    div:nth-of-type(2) {
      margin-top: 0;
      margin-bottom: 16px;
    }
  }
`

const DateOfBirthQuestion = ({ existingAnswers, updateFollowingPrefillStep }: QuestionComponentProps): JSX.Element => {
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const attribution = useContext(AttributionContext)
  const { dispatch, featureSwitches } = useContext(ApplicationFormContext)
  const {
    removeContinueCtaV1,
    reviewPageV3,
    questionCopyChangeV1
  } = featureSwitches ?? {}
  const [answers, setAnswers] = useState(existingAnswers || {
    day: '',
    month: '',
    year: ''
  })
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({
    dateOfBirth: ''
  })
  const [isAllAnswered, setIsAllAnswered] = useState(false)
  const [nextButtonCb, resetNextQuestionCb] = useCallbackOnce()
  const [previousButtonCb, resetPreviousQuestionCb] = useCallbackOnce()
  const validationSchema = DateOfBirthValidator(attribution).getValidator()
  const reviewPageV3AndClickedRequired = reviewPageV3 && hasNavigatedFromRequiredClick

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('dateOfBirth')
    }
  }, [])

  const validate = (): boolean => {
    try {
      validationSchema.validateSync({
        dateOfBirth: `${answers.day}-${answers.month}-${answers.year}`
      }, { abortEarly: false })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      const prettyErrors: Record<string, string> = {}
      if (!err.inner) return false

      err.inner.forEach((validationError, index) => {
        if (!validationError.path) return

        if (index === 0) {
          document.getElementById(validationError.path)?.scrollIntoView()
        }

        const [errorMessage] = validationError.errors
        prettyErrors[validationError.path.toString()] = errorMessage
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Validation Error',
          label: validationError.path.toString(),
          nonInteraction: true
        })
      })
      setInputErrors(prettyErrors)
      return false
    }

    return true
  }

  const handleInputChange = (value: DateOfBirthValues) => {
    setAnswers((prev: DateOfBirthValues) => ({ ...prev, ...value }))
  }

  const moveToNextQuestion = () => {
    const valid = validate()
    if (!valid) {
      resetNextQuestionCb()
      return
    }
    if (valid) {
      const dobCategory = calculateDOBCategory(answers)
      fireReactGAEvent({
        category: 'Application Form',
        action: 'category-d-assigned',
        label: dobCategory
      })
    }

    dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.DATE_OF_BIRTH,
      value: answers
    })

    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (reviewPageV3AndClickedRequired || !hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
    }
  }

  const moveToPreviousQuestion = () => {
    const valid = validate()
    if (!valid && (reviewPageV3)) {
      resetPreviousQuestionCb()
      return
    }
    handlePrevButtonClick(
      dispatch,
      { name: QuestionKey.DATE_OF_BIRTH, value: answers }
    )
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, false)
      }
    }
  }

  const handleEnterKeyPress = (e: React.KeyboardEvent) => {
    if ((e.code === 'Enter' || e.key === 'Enter') && isAllAnswered) {
      nextButtonCb(moveToNextQuestion)
    }
  }

  useEffect(() => {
    if (Object.keys(answers).every((key) => !!answers[key])) {
      setIsAllAnswered(true)
    } else {
      setIsAllAnswered(false)
    }
  }, [answers])

  const handleDateOfBirthAutofocus = () => {
    const isMobile = isMobileOrTabletDevice()
    return (isMobile && removeContinueCtaV1) ? false : true
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <QuestionTitleWrapper gridColumn={['auto / span 12', '3 / span 8']} className={questionCopyChangeV1 ? 'questionCopyChangeV1' : ''}>
          <AppFormQuestion
            questionKey={QuestionKey.DATE_OF_BIRTH}
            mt={3}
            mb={2}
            textStyle={questionFontStyle}
          >
            {questionCopyChangeV1 ? 'What\'s your date of birth?' : 'Your date of birth'}
          </AppFormQuestion>
          <Box
            mt={2}
            mb={2}
            textStyle="body.regular.regular"
            color="#51585b"
          >
            e.g. 30 5 1987
          </Box>
        </QuestionTitleWrapper>

        <MuiDateOfBirth
          defaultValue={existingAnswers}
          valueChanged={(value: DateOfBirthValues) => handleInputChange(value)}
          error={inputErrors.dateOfBirth}
          onKeyPressHandler={handleEnterKeyPress}
          dobAutoFocus={(reviewPageV3) ? false : handleDateOfBirthAutofocus()}
        />
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(moveToNextQuestion)}
        previousHandler={() => previousButtonCb(moveToPreviousQuestion)}
        disableNext={!isAllAnswered}
        disablePrevious={(reviewPageV3) ? !isAllAnswered : false}
      />
    </>
  )
}

export default withFade(withQuestion(DateOfBirthQuestion))
